/**
 * Loading faces, one per panel, each shaped like what will land in it: the
 * waiting cards with their evidence column, the steering timeline's rows, the
 * report document with its index, the history table. Still blocks, no shimmer
 * and no spinner; the panel carries aria-busy, so nothing here is announced.
 */

function Bar({ w, h = 10, className }: { w: string; h?: number; className?: string }) {
  return (
    <span
      className={className ? `sig-skel ${className}` : "sig-skel"}
      style={{ width: w, height: h }}
    />
  );
}

/** Signals / Waiting card · Skeleton: three cards, the read (meta, two title
 *  lines, three body lines, two buttons) beside a 400 evidence column. */
export function WaitingSkeleton() {
  return (
    <div className="sig-queue" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="sig-card--skel">
          <div className="sig-skelcard__read">
            <div className="sig-skelcard__row">
              <Bar w="80px" />
              <Bar w="60px" />
            </div>
            <Bar w="100%" h={18} />
            <Bar w="320px" h={18} />
            <span className="sig-skelcard__gap" />
            <Bar w="100%" />
            <Bar w="100%" />
            <Bar w="260px" />
            <span className="sig-skelcard__gap" />
            <div className="sig-skelcard__row">
              <Bar w="96px" h={32} className="sig-skel--pill" />
              <Bar w="96px" h={32} className="sig-skel--pill" />
            </div>
          </div>
          <div className="sig-skelcard__ev">
            <Bar w="90px" />
            <span className="sig-skelcard__gap sig-skelcard__gap--s" />
            <Bar w="100%" />
            <Bar w="100%" />
            <Bar w="220px" />
            <span className="sig-skelcard__gap sig-skelcard__gap--s" />
            <Bar w="100%" />
            <Bar w="100%" />
            <Bar w="180px" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SteeringSkeleton() {
  return (
    <div className="sig-steer" aria-hidden="true">
      <div className="sig-steer__head">
        <Bar w="180px" />
        <span />
        <Bar w="60px" className="sig-skel--end" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="sig-steer__row">
          <div className="sig-steer__grid">
            <div className="sig-steer__main">
              <Bar w="140px" />
              <Bar w="80%" h={20} />
              <Bar w="94%" h={11} />
            </div>
            <div className="sig-steer__run">
              <Bar w={`${60 - i * 15}%`} h={5} className="sig-skel--run" />
            </div>
            <div className="sig-steer__days">
              <Bar w="44px" h={30} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReportsSkeleton() {
  return (
    <div className="sig-brief" aria-hidden="true">
      <div className="sig-brief__mast">
        <Bar w="64%" h={40} />
        <Bar w="46%" h={10} className="sig-skel--gap-s" />
      </div>
      <div className="sig-brief__main">
        {[0, 1].map((i) => (
          <div key={i} className="sig-brief__section">
            <Bar w="160px" h={12} />
            <Bar w="72%" h={26} className="sig-skel--gap-s" />
            <Bar w="96%" h={13} className="sig-skel--gap-s" />
            <Bar w="88%" h={13} className="sig-skel--gap-s" />
            <Bar w="60%" h={13} className="sig-skel--gap-s" />
          </div>
        ))}
      </div>
      <div className="sig-brief__rail">
        <div className="sig-reports__index">
          <Bar w="120px" />
          {[0, 1].map((i) => (
            <div key={i} className="sig-reports__item sig-skel-item">
              <Bar w="90px" />
              <Bar w="150px" h={13} />
              <Bar w="200px" h={11} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HistorySkeleton() {
  return (
    <div className="sig-hist" aria-hidden="true">
      <div className="sig-chips">
        <Bar w="56px" />
        <Bar w="96px" />
        <Bar w="80px" />
      </div>
      <div className="sig-table">
        <div className="sig-table__head">
          <Bar w="40px" />
          <Bar w="60px" />
          <Bar w="50px" />
          <Bar w="80px" />
          <span />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="sig-table__row">
            <div className="sig-table__line sig-table__line--skel">
              <Bar w="84px" />
              <Bar w="130px" h={12} />
              <span className="sig-table__sig">
                <Bar w="70%" h={16} />
                <Bar w="80px" />
              </span>
              <Bar w="96px" h={12} />
              <span />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The phone deck while the queue loads: the greeting row, progress, the card with its two buttons. */
export function DeckSkeleton() {
  return (
    <div className="sig-deck sig-deck--skel" aria-hidden="true">
      <div className="sig-deck__head">
        <Bar w="58%" h={20} />
        <Bar w="44px" h={10} />
      </div>
      <div className="sig-deck__progress">
        <span className="sig-deck__seg" />
        <span className="sig-deck__seg" />
        <span className="sig-deck__seg" />
      </div>
      <div className="sig-deck__stack">
        <div className="sig-deck__card is-top has-controls">
          <div className="sig-deck__clip">
            <div className="sig-deck__hero">
              <Bar w="40%" />
              <div>
                <Bar w="88%" h={30} />
                <Bar w="60%" h={12} className="sig-skel--gap-s" />
              </div>
            </div>
            <div className="sig-deck__reading">
              <Bar w="94%" h={13} />
              <Bar w="80%" h={13} className="sig-skel--gap-s" />
            </div>
          </div>
        </div>
        <div className="sig-deck__controls">
          <div className="sig-deck__buttons">
            <Bar w="52px" h={52} />
            <Bar w="52px" h={52} />
          </div>
        </div>
      </div>
    </div>
  );
}
